import { useCallback, useEffect, useRef, useState, type ReactNode } from "react"

import { Button, Loader } from "@coval/ui"
import { Pause, Play, Volume2 } from "lucide-react"

import { logger } from "@/lib/logger"

const log = logger("AudioPreviewButton")

// HTMLAudioElement.volume cannot amplify above 1.0; GainNode can.
const MAX_GAIN_MULTIPLIER = 2
const PROXIED_AUDIO_PREVIEW_PREFIXES = [
  "https://coval-voice-previews.s3.us-east-2.amazonaws.com/voice-previews-v2/",
  "https://coval-background-sound-previews.s3.us-east-2.amazonaws.com/",
] as const

declare global {
  interface Window {
    webkitAudioContext?: typeof AudioContext
  }
}

function resolveAudioContextConstructor(): typeof AudioContext | undefined {
  if (typeof window === "undefined") {
    return undefined
  }
  const candidate = window.AudioContext ?? window.webkitAudioContext
  return typeof candidate === "function" ? candidate : undefined
}

export function resolveAudioPreviewSourceUrl(previewUrl: string): string {
  let parsed: URL
  try {
    parsed = new URL(previewUrl)
  } catch {
    return previewUrl
  }

  const source = parsed.toString()
  if (
    PROXIED_AUDIO_PREVIEW_PREFIXES.some((prefix) => source.startsWith(prefix))
  ) {
    return `/api/audio-preview?source=${encodeURIComponent(source)}`
  }

  return previewUrl
}

function canUseWebAudioForSource(sourceUrl: string): boolean {
  if (typeof window === "undefined") {
    return false
  }

  try {
    const parsed = new URL(sourceUrl, window.location.href)
    return (
      parsed.origin === window.location.origin || parsed.protocol === "blob:"
    )
  } catch {
    return false
  }
}

interface AudioPreviewButtonProps {
  previewUrl?: string
  audioLabel: string
  className?: string
  volume?: number
  /**
   * HTMLAudioElement.playbackRate. 1.0 = original speed.
   * Browsers distort heavily outside roughly 0.5–4 — callers should clamp to
   * the bounds exposed by the persona slider.
   */
  playbackRate?: number
  currentlyPlayingId?: string | null
  onPlayingChange?: (playingId: string | null) => void
  tooltipContent?: ReactNode
}

export function AudioPreviewButton({
  previewUrl,
  audioLabel,
  className = "",
  volume = 1.0,
  playbackRate = 1.0,
  currentlyPlayingId,
  onPlayingChange,
  tooltipContent,
}: AudioPreviewButtonProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [isPlayingInternal, setIsPlayingInternal] = useState(false)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const audioContextRef = useRef<AudioContext | null>(null)
  const gainNodeRef = useRef<GainNode | null>(null)
  const mediaSourceRef = useRef<MediaElementAudioSourceNode | null>(null)
  const audioSourceUrlRef = useRef<string | null>(null)
  const audioEventHandlersRef = useRef<{
    ended: () => void
    error: () => void
    loadstart: () => void
    canplay: () => void
  } | null>(null)

  const isControlled = currentlyPlayingId !== undefined
  const isPlaying = isControlled
    ? currentlyPlayingId === previewUrl
    : isPlayingInternal

  const stopPlaying = useCallback(() => {
    setIsPlayingInternal(false)
    onPlayingChange?.(null)
  }, [onPlayingChange])

  useEffect(() => {
    if (isControlled && currentlyPlayingId !== previewUrl && audioRef.current) {
      audioRef.current.pause()
      setIsPlayingInternal(false)
    }
  }, [currentlyPlayingId, previewUrl, isControlled])

  const disconnectAudioGraph = useCallback(() => {
    try {
      mediaSourceRef.current?.disconnect()
    } catch {
      // Best-effort cleanup only.
    }
    try {
      gainNodeRef.current?.disconnect()
    } catch {
      // Best-effort cleanup only.
    }
    mediaSourceRef.current = null
    gainNodeRef.current = null
  }, [])

  const releaseAudioElement = useCallback(() => {
    const audio = audioRef.current
    if (audio) {
      const handlers = audioEventHandlersRef.current
      if (handlers) {
        audio.removeEventListener("ended", handlers.ended)
        audio.removeEventListener("error", handlers.error)
        audio.removeEventListener("loadstart", handlers.loadstart)
        audio.removeEventListener("canplay", handlers.canplay)
      }
      audio.pause()
    }

    audioEventHandlersRef.current = null
    audioRef.current = null
    audioSourceUrlRef.current = null
    disconnectAudioGraph()
  }, [disconnectAudioGraph])

  useEffect(() => {
    return () => {
      releaseAudioElement()
      const audioContext = audioContextRef.current
      audioContextRef.current = null
      if (audioContext && audioContext.state !== "closed") {
        void audioContext.close()
      }
    }
  }, [releaseAudioElement])

  useEffect(() => {
    if (
      isControlled &&
      currentlyPlayingId === previewUrl &&
      !audioRef.current
    ) {
      onPlayingChange?.(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- legacy dependency suppression pending refactor
  }, [])

  useEffect(() => {
    const clampedGain = Math.max(0, Math.min(MAX_GAIN_MULTIPLIER, volume))
    if (gainNodeRef.current) {
      gainNodeRef.current.gain.value = clampedGain
      if (audioRef.current) {
        audioRef.current.volume = 1
      }
      return
    }
    if (audioRef.current) {
      audioRef.current.volume = Math.max(0, Math.min(1, volume))
    }
  }, [volume])

  // Mirror the same real-time effect for playback rate.
  useEffect(() => {
    if (audioRef.current) {
      const clampedRate = Math.max(0.25, Math.min(4, playbackRate))
      audioRef.current.playbackRate = clampedRate
    }
  }, [playbackRate])

  const handleTogglePlay = useCallback(async () => {
    if (!previewUrl) {
      return
    }

    const audioSourceUrl = resolveAudioPreviewSourceUrl(previewUrl)

    if (audioSourceUrlRef.current !== audioSourceUrl) {
      releaseAudioElement()

      const audio = new Audio()
      audio.src = audioSourceUrl
      audioRef.current = audio
      audioSourceUrlRef.current = audioSourceUrl

      const handlers = {
        ended: () => {
          if (audioRef.current !== audio) {
            return
          }
          stopPlaying()
        },
        error: () => {
          if (audioRef.current !== audio) {
            return
          }
          setIsLoading(false)
          stopPlaying()
          log.warn("Failed to load audio preview", {
            audioLabel,
            sourceUrl: audioSourceUrl,
            url: previewUrl,
          })
        },
        loadstart: () => {
          if (audioRef.current !== audio) {
            return
          }
          setIsLoading(true)
        },
        canplay: () => {
          if (audioRef.current !== audio) {
            return
          }
          setIsLoading(false)
        },
      }
      audioEventHandlersRef.current = handlers
      audio.addEventListener("ended", handlers.ended)
      audio.addEventListener("error", handlers.error)
      audio.addEventListener("loadstart", handlers.loadstart)
      audio.addEventListener("canplay", handlers.canplay)
    }

    if (!audioRef.current) {
      return
    }

    const currentAudioSourceUrl = audioSourceUrlRef.current
    if (
      !gainNodeRef.current &&
      currentAudioSourceUrl &&
      canUseWebAudioForSource(currentAudioSourceUrl)
    ) {
      const AudioContextCtor = resolveAudioContextConstructor()
      if (AudioContextCtor) {
        try {
          if (!audioContextRef.current) {
            audioContextRef.current = new AudioContextCtor()
          }
          const audioContext = audioContextRef.current
          const source = audioContext.createMediaElementSource(audioRef.current)
          const gain = audioContext.createGain()
          gain.gain.value = Math.max(0, Math.min(MAX_GAIN_MULTIPLIER, volume))
          source.connect(gain)
          gain.connect(audioContext.destination)
          mediaSourceRef.current = source
          gainNodeRef.current = gain
        } catch (error) {
          log.warn(
            "Failed to wire Web Audio gain chain - falling back to native volume",
            {
              audioLabel,
              error: error instanceof Error ? error.message : String(error),
            },
          )
        }
      }
    }

    if (gainNodeRef.current) {
      gainNodeRef.current.gain.value = Math.max(
        0,
        Math.min(MAX_GAIN_MULTIPLIER, volume),
      )
      audioRef.current.volume = 1
    } else {
      audioRef.current.volume = Math.max(0, Math.min(1, volume))
    }
    audioRef.current.playbackRate = Math.max(0.25, Math.min(4, playbackRate))

    if (audioContextRef.current?.state === "suspended") {
      try {
        await audioContextRef.current.resume()
      } catch (error) {
        log.warn("Failed to resume AudioContext", {
          audioLabel,
          error: error instanceof Error ? error.message : String(error),
        })
      }
    }

    if (isPlaying) {
      audioRef.current.pause()
      stopPlaying()
    } else {
      try {
        audioRef.current.currentTime = 0
        await audioRef.current.play()
        setIsPlayingInternal(true)
        onPlayingChange?.(previewUrl)
      } catch (error) {
        log.warn("Failed to play audio preview", {
          audioLabel,
          error: error instanceof Error ? error.message : String(error),
        })
        stopPlaying()
      }
    }
  }, [
    previewUrl,
    volume,
    playbackRate,
    isPlaying,
    audioLabel,
    onPlayingChange,
    stopPlaying,
    releaseAudioElement,
  ])

  // No preview available
  if (!previewUrl) {
    return (
      <Button
        type="button"
        variant="secondary"
        icon
        disabled
        className={className}
        tooltip={`Audio preview not available for ${audioLabel}`}
      >
        <Volume2 className="w-4 h-4 opacity-40" />
      </Button>
    )
  }

  const previewButton = (
    <Button
      type="button"
      variant="secondary"
      icon
      disabled={isLoading}
      onClick={(e) => {
        e.stopPropagation()
        void handleTogglePlay()
      }}
      onPointerDown={(e) => {
        e.stopPropagation()
      }}
      onPointerUp={(e) => {
        e.stopPropagation()
      }}
      className={className}
      aria-label={
        isPlaying ? `Pause ${audioLabel} preview` : `Play ${audioLabel} preview`
      }
      // Callers with something longer to say replace the label outright rather
      // than stacking a second tooltip on top of it.
      tooltip={
        tooltipContent ? (
          <div className="max-w-xs leading-5">{tooltipContent}</div>
        ) : isPlaying ? (
          `Pause ${audioLabel} preview`
        ) : (
          `Play ${audioLabel} preview`
        )
      }
    >
      {isLoading ? (
        <Loader size="sm" label="Loading audio" />
      ) : isPlaying ? (
        <Pause className="w-4 h-4" />
      ) : (
        <Play className="w-4 h-4" />
      )}
    </Button>
  )

  return previewButton
}
